import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  CheckCircle2,
  Clock3,
  FileCheck2,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  ShieldCheck,
  Users,
} from 'lucide-react';

import {
  getContractorPortalCandidate,
  getContractorPortalOverview,
  getContractorPortalWorkforce,
  inviteContractorCandidate,} from '@/api/contractorOnboardingPortalApi';


const valueOrDash = (value) => (
  value === null
  || value === undefined
  || value === ''
    ? '—'
    : value
);


const dateValue = (value) => {
  if (!value) {
    return '—';
  }

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};


const Status = ({ value }) => {
  const text = String(
    value || 'unknown'
  );

  return (
    <span className="inline-flex rounded-full border px-2.5 py-1 text-xs font-medium">
      {text.replaceAll('_', ' ')}
    </span>
  );
};


export default function ContractorOnboardingPortal() {
  const [data, setData] = useState(null);

  // IRIS_R6_E_R1_WORKFORCE_STATE
  const [
    approvedWorkforce,
    setApprovedWorkforce,
  ] = useState([]);

  const [
    workforceBoundary,
    setWorkforceBoundary,
  ] = useState(null);


  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeTab, setActiveTab] = useState('overview');

  const [selectedId, setSelectedId] = useState('');
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [candidateLoading, setCandidateLoading] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [ttlHours, setTtlHours] = useState(72);
  const [sending, setSending] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');


  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getContractorPortalOverview();
      setData(result);

      /* IRIS_R6_E_R1_WORKFORCE_FETCH */
      try {
        const workforcePayload =
          await getContractorPortalWorkforce();

        setApprovedWorkforce(
          workforcePayload?.workforce || []
        );

        setWorkforceBoundary(
          workforcePayload?.boundary || null
        );

      } catch (workforceError) {

        console.error(
          'Unable to load approved contractor workforce:',
          workforceError
        );

        setApprovedWorkforce([]);
        setWorkforceBoundary(null);
      }

    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to load contractor onboarding.'
      );
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    load();
  }, [load]);


  const contractor = data?.contractor || {};

  // IRIS_R7_R2_ACTIVE_VIEW_FILTERING
  //
  // Retirement is authoritative database state.
  // This projection only prevents retired/revoked test journeys from
  // appearing as current operational onboarding work.
  // Historical records remain persisted and available to audit/history
  // surfaces.
  const RETIRED_ONBOARDING_STATES = new Set([
    'revoked',
    'retired',
    'retired_test_identity',
    'cancelled_test_retirement',
    'cancelled',
  ]);

  const normaliseLifecycleState = (value) => (
    String(value || '')
      .trim()
      .toLowerCase()
  );

  const hasRetiredLifecycleState = (item) => {
    if (!item) {
      return false;
    }

    const workforceAdmission =
      item.workforce_admission
      || item.workforceAdmission
      || {};

    const operationalActivation =
      item.operational_activation
      || item.operationalActivation
      || {};

    const states = [
      item.status,
      item.invitation_status,
      item.invitationStatus,
      item.candidate_status,
      item.candidateStatus,
      item.approval_status,
      item.approvalStatus,
      item.workforce_status,
      item.workforceStatus,
      item.identity_status,
      item.identityStatus,
      item.operational_identity_status,
      item.operationalIdentityStatus,
      item.delivery_status,
      item.deliveryStatus,
      workforceAdmission.status,
      operationalActivation.status,
      operationalActivation.identity_status,
    ];

    return states.some(
      (state) => (
        RETIRED_ONBOARDING_STATES.has(
          normaliseLifecycleState(state)
        )
      )
    );
  };

  const allInvitations =
    data?.invitations || [];

  const allCandidates =
    data?.candidates || [];

  const invitations =
    allInvitations.filter(
      (item) => !hasRetiredLifecycleState(item)
    );

  const candidates =
    allCandidates.filter(
      (item) => !hasRetiredLifecycleState(item)
    );

  const retiredInvitations =
    allInvitations.filter(
      hasRetiredLifecycleState
    );

  const retiredCandidates =
    allCandidates.filter(
      hasRetiredLifecycleState
    );


  // R7-R2 intentionally retains historical rows in the loaded payload.
  // These values are not rendered as active operational work but remain
  // useful for future History/Audit surfaces.
  const retiredRecordCount =
    retiredInvitations.length
    + retiredCandidates.length;


  const selectedCandidate = useMemo(
    () => (
      candidates.find(
        (item) => (
          item.invitation_id === selectedId
        )
      ) || null
    ),
    [
      candidates,
      selectedId,
    ],
  );


  const openCandidate = async (invitationId) => {
    setSelectedId(invitationId);
    setCandidateDetail(null);
    setCandidateLoading(true);

    try {
      const result = await getContractorPortalCandidate(
        invitationId
      );

      setCandidateDetail(result);
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || 'Unable to load candidate.'
      );
    } finally {
      setCandidateLoading(false);
    }
  };


  const sendInvitation = async (event) => {
    event.preventDefault();

    if (!candidateEmail.trim()) {
      return;
    }

    setSending(true);
    setInviteMessage('');

    try {
      const result = await inviteContractorCandidate({
        contractorId: contractor.id,
        candidateName: candidateName.trim(),
        candidateEmail: candidateEmail.trim(),
        ttlHours: Number(ttlHours) || 72,
      });

      if (
        result?.email_sent !== true
        || result?.delivery_status !== 'sent'
      ) {
        throw new Error(
          'Invitation was not confirmed as delivered.'
        );
      }

      setInviteMessage(
        'Invitation sent successfully.'
      );

      setCandidateName('');
      setCandidateEmail('');
      setTtlHours(72);

      await load();

    } catch (err) {
      setInviteMessage(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to send invitation.'
      );

    } finally {
      setSending(false);
    }
  };


  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }


  return (
    <div className="space-y-6">

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Contractor Portal
          </p>

          <h1 className="mt-1 text-3xl font-bold tracking-tight">
            Contractor Onboarding
          </h1>

          <p className="mt-2 text-muted-foreground">
            {valueOrDash(
              contractor.trading_name
              || contractor.legal_name
            )}
          </p>
        </div>

        <div className="flex gap-2">

          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>

          <button
            type="button"
            onClick={() => {
              setInviteOpen(
                (value) => !value
              );
              setInviteMessage('');
            }}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            <Send className="h-4 w-4" />
            Invite Candidate
          </button>

        </div>

      </div>


      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm">
          {error}
        </div>
      )}


      <div className="grid gap-4 sm:grid-cols-3">

        <div className="rounded-xl border bg-card p-5">
          <Mail className="h-5 w-5 text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">
            Invitations
          </p>
          <p className="mt-1 text-3xl font-bold">
            {data?.counts?.invitations ?? 0}
          </p>
        </div>

        <div className="rounded-xl border bg-card p-5">
          <Users className="h-5 w-5 text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">
            Candidates
          </p>
          <p className="mt-1 text-3xl font-bold">
            {data?.counts?.candidates ?? 0}
          </p>
        </div>

        <div className="rounded-xl border bg-card p-5">
          <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">
            Completed
          </p>
          <p className="mt-1 text-3xl font-bold">
            {data?.counts?.completed ?? 0}
          </p>
        </div>

      </div>


      {inviteOpen && (
        <form
          onSubmit={sendInvitation}
          className="rounded-xl border bg-card p-5"
        >

          <div className="mb-4">
            <h2 className="text-lg font-semibold">
              Invite Candidate
            </h2>

            <p className="mt-1 text-sm text-muted-foreground">
              Invitation authority is restricted to your organisation.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Candidate name
              </span>
              <input
                value={candidateName}
                onChange={(e) => setCandidateName(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2"
                maxLength={200}
              />
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Candidate email
              </span>
              <input
                type="email"
                required
                value={candidateEmail}
                onChange={(e) => setCandidateEmail(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2"
              />
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Invitation validity
              </span>
              <select
                value={ttlHours}
                onChange={(e) => setTtlHours(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2"
              >
                <option value={24}>24 hours</option>
                <option value={48}>48 hours</option>
                <option value={72}>72 hours</option>
                <option value={120}>5 days</option>
                <option value={168}>7 days</option>
              </select>
            </label>

          </div>

          <div className="mt-4 flex items-center gap-3">

            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {
                sending
                  ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )
                  : (
                    <Send className="h-4 w-4" />
                  )
              }

              Create & Send Invitation
            </button>

            {inviteMessage && (
              <p className="text-sm">
                {inviteMessage}
              </p>
            )}

          </div>

        </form>
      )}


      <div className="flex flex-wrap gap-2 rounded-lg border bg-card p-2">

        {[
          ['overview', 'Overview'],
          ['invitations', 'Invitations'],
          ['candidates', 'Candidates'],
          ['workforce', 'Workforce'],
        ].map(
          ([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setActiveTab(value)}
              className={
                `rounded-md px-4 py-2 text-sm font-medium ${
                  activeTab === value
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`
              }
            >
              {label}
            </button>
          )
        )}

      </div>


      {activeTab === 'overview' && (
        <div className="grid gap-4 lg:grid-cols-2">

          <div className="rounded-xl border bg-card p-5">
            <ShieldCheck className="h-6 w-6" />

            <h2 className="mt-4 text-lg font-semibold">
              Organisation
            </h2>

            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="text-muted-foreground">
                  Trading name
                </dt>
                <dd className="font-medium">
                  {valueOrDash(contractor.trading_name)}
                </dd>
              </div>

              <div>
                <dt className="text-muted-foreground">
                  Legal name
                </dt>
                <dd className="font-medium">
                  {valueOrDash(contractor.legal_name)}
                </dd>
              </div>

              <div>
                <dt className="text-muted-foreground">
                  Onboarding
                </dt>
                <dd className="font-medium">
                  {
                    contractor.onboarding_enabled
                      ? 'Enabled'
                      : 'Disabled'
                  }
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-xl border bg-card p-5">
            <Clock3 className="h-6 w-6" />

            <h2 className="mt-4 text-lg font-semibold">
              Recent activity
            </h2>

            <div className="mt-4 space-y-3">

              {invitations.slice(0, 5).map(
                (item) => (
                  <div
                    key={item.id}
                    className="rounded-lg border p-3"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium">
                          {
                            item.candidate_name
                            || item.candidate_email
                          }
                        </p>

                        <p className="mt-1 text-xs text-muted-foreground">
                          {item.candidate_email}
                        </p>
                      </div>

                      <Status value={item.status} />
                    </div>
                  </div>
                )
              )}

              {invitations.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No invitations recorded.
                </p>
              )}

            </div>
          </div>

        </div>
      )}


      {activeTab === 'invitations' && (
        <div className="overflow-hidden rounded-xl border bg-card">

          <div className="border-b p-5">
            <h2 className="text-lg font-semibold">
              Invitations
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left">Candidate</th>
                  <th className="px-4 py-3 text-left">Invitation</th>
                  <th className="px-4 py-3 text-left">Delivery</th>
                  <th className="px-4 py-3 text-left">Created</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {invitations.map(
                  (item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3">
                        <p className="font-medium">
                          {
                            item.candidate_name
                            || 'Unnamed candidate'
                          }
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.candidate_email}
                        </p>
                      </td>

                      <td className="px-4 py-3">
                        <Status value={item.status} />
                      </td>

                      <td className="px-4 py-3">
                        <Status value={item.delivery_status} />
                      </td>

                      <td className="px-4 py-3">
                        {dateValue(item.created_at)}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

        </div>
      )}


      {activeTab === 'candidates' && (
        <div className="grid gap-4 xl:grid-cols-[1fr_1.2fr]">

          <div className="rounded-xl border bg-card">

            <div className="border-b p-5">
              <h2 className="text-lg font-semibold">
                Candidates
              </h2>
            </div>

            <div className="divide-y">
              {candidates.map(
                (item) => (
                  <button
                    key={item.invitation_id}
                    type="button"
                    onClick={() => openCandidate(item.invitation_id)}
                    className="block w-full p-4 text-left hover:bg-muted/50"
                  >
                    <div className="flex items-start justify-between gap-3">

                      <div>
                        <p className="font-medium">
                          {
                            item.candidate_name
                            || item.candidate_email
                          }
                        </p>

                        <p className="mt-1 text-xs text-muted-foreground">
                          {item.candidate_email}
                        </p>

                        <p className="mt-2 text-xs text-muted-foreground">
                          Evidence: {item.accepted_evidence_count}/{item.evidence_count} accepted
                        </p>
                      </div>

                      <Status value={item.invitation_status} />

                    </div>
                  </button>
                )
              )}

              {candidates.length === 0 && (
                <p className="p-5 text-sm text-muted-foreground">
                  No candidates recorded.
                </p>
              )}

            </div>

          </div>


          <div className="rounded-xl border bg-card p-5">

            {!selectedId && (
              <div className="flex min-h-[240px] items-center justify-center text-center text-sm text-muted-foreground">
                Select a candidate to view onboarding progress.
              </div>
            )}

            {candidateLoading && (
              <div className="flex min-h-[240px] items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}

            {
              selectedCandidate
              && candidateDetail
              && !candidateLoading
              && (
                <div>

                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-semibold">
                        {
                          selectedCandidate.candidate_name
                          || selectedCandidate.candidate_email
                        }
                      </h2>

                      <p className="mt-1 text-sm text-muted-foreground">
                        {selectedCandidate.candidate_email}
                      </p>
                    </div>

                    <Status value={selectedCandidate.invitation_status} />
                  </div>

                  <div className="mt-6 rounded-lg border p-4">

                    <div className="flex items-center gap-2">
                      <FileCheck2 className="h-5 w-5" />

                      <h3 className="font-semibold">
                        Evidence
                      </h3>
                    </div>

                    <div className="mt-4 space-y-3">

                      {(candidateDetail.evidence || []).map(
                        (item) => (
                          <div
                            key={item.id || `${item.document_type}-${item.version}`}
                            className="flex items-center justify-between gap-4 rounded-lg border p-3"
                          >
                            <div>
                              <p className="font-medium">
                                {valueOrDash(item.document_type)}
                              </p>

                              <p className="mt-1 text-xs text-muted-foreground">
                                {valueOrDash(item.original_filename)}
                              </p>
                            </div>

                            <Status value={item.review_status} />
                          </div>
                        )
                      )}

                      {
                        (candidateDetail.evidence || []).length === 0
                        && (
                          <p className="text-sm text-muted-foreground">
                            No evidence uploaded yet.
                          </p>
                        )
                      }

                    </div>

                  </div>

                  <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
                    Final compliance approval is performed by BSMS as principal.
                  </div>

                </div>
              )
            }

          </div>

        </div>
      )}


      {/* IRIS_R6_E_R1_APPROVED_WORKFORCE_UI */}
      {activeTab === 'workforce' && (
        <div className="rounded-xl border bg-card">

          <div className="border-b p-5">

            <h2 className="text-lg font-semibold">
              Approved Workforce
            </h2>

            <p className="mt-1 text-sm text-muted-foreground">
              Candidates approved by BSMS and admitted
              to your contractor workforce.
            </p>

          </div>


          <div className="p-5">

            {approvedWorkforce.length === 0 ? (

              <div className="rounded-lg border border-dashed p-8 text-center">

                <Users className="mx-auto h-7 w-7 text-muted-foreground" />

                <p className="mt-3 font-medium">
                  No approved workforce members
                </p>

                <p className="mt-1 text-sm text-muted-foreground">
                  Approved candidates will appear here
                  after principal review.
                </p>

              </div>

            ) : (

              <div className="space-y-4">

                {approvedWorkforce.map(
                  (worker) => {

                    const activation =
                      worker?.operational_activation
                      || {};

                    return (

                      <div
                        key={
                          worker.approval_id
                          || worker.invitation_id
                        }
                        className="rounded-lg border p-5"
                      >

                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">

                          <div>

                            <h3 className="font-semibold">
                              {worker.candidate_name}

                              {
                                worker.preferred_name
                                  ? ` (${worker.preferred_name})`
                                  : ''
                              }
                            </h3>

                            <p className="mt-1 text-sm text-muted-foreground">
                              {worker.candidate_email}
                            </p>

                          </div>


                          <div className="flex flex-wrap gap-2">

                            <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-600">
                              Workforce admitted
                            </span>

                            <span
                              className={
                                activation.active
                                  ? 'rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-600'
                                  : 'rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-600'
                              }
                            >
                              {
                                activation.active
                                  ? 'Operational access active'
                                  : 'Operational access not enabled'
                              }
                            </span>

                          </div>

                        </div>


                        <div className="mt-5 grid gap-3 md:grid-cols-3">

                          <div className="rounded-lg border p-3">

                            <p className="text-xs text-muted-foreground">
                              Operational account
                            </p>

                            <p className="mt-1 text-sm font-semibold">
                              {
                                activation.account_created
                                  ? 'Created'
                                  : 'Not created'
                              }
                            </p>

                          </div>


                          <div className="rounded-lg border p-3">

                            <p className="text-xs text-muted-foreground">
                              Roster access
                            </p>

                            <p className="mt-1 text-sm font-semibold">
                              {
                                activation.roster_access
                                  ? 'Enabled'
                                  : 'Not enabled'
                              }
                            </p>

                          </div>


                          <div className="rounded-lg border p-3">

                            <p className="text-xs text-muted-foreground">
                              Site access
                            </p>

                            <p className="mt-1 text-sm font-semibold">
                              {
                                activation.site_access
                                  ? 'Enabled'
                                  : 'Not enabled'
                              }
                            </p>

                          </div>

                        </div>


                        <p className="mt-4 text-xs text-muted-foreground">
                          Approved
                          {
                            worker.approved_by
                              ? ` by ${worker.approved_by}`
                              : ''
                          }
                          {
                            worker.approved_at
                              ? ` • ${new Date(
                                  worker.approved_at
                                ).toLocaleString()}`
                              : ''
                          }
                        </p>

                      </div>
                    );
                  }
                )}

              </div>
            )}


            {workforceBoundary && (

              <div className="mt-5 rounded-lg border border-blue-500/20 bg-blue-500/5 p-4">

                <div className="flex gap-3">

                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />

                  <div>

                    <p className="text-sm font-semibold">
                      Operational activation boundary
                    </p>

                    <p className="mt-1 text-sm text-muted-foreground">
                      Workforce approval does not automatically
                      create a login account, enable roster access,
                      or grant site access. Operational activation
                      remains a separate controlled process.
                    </p>

                  </div>

                </div>

              </div>

            )}

          </div>

        </div>
      )}


    </div>
  );
}
